import { assert } from 'assert-ts';

/**
 * Friendly, hostname-safe word lists for auto-generating a default host name in the get-started
 * flow (e.g. `eager-falcon`). Lowercase single words so the result needs no sanitizing and reads
 * as an obviously-generated default the user can rename later. 20 × 20 = 400 combinations.
 */
const ADJECTIVES = [
  'brave',
  'calm',
  'clever',
  'eager',
  'gentle',
  'jolly',
  'keen',
  'lively',
  'mellow',
  'nimble',
  'plucky',
  'quiet',
  'merry',
  'snug',
  'spry',
  'sunny',
  'swift',
  'witty',
  'zesty',
  'bold',
] as const;

const NOUNS = [
  'otter',
  'falcon',
  'hawk',
  'fox',
  'lynx',
  'heron',
  'robin',
  'badger',
  'ferret',
  'gecko',
  'ibis',
  'koala',
  'lemur',
  'narwhal',
  'osprey',
  'puffin',
  'raven',
  'seal',
  'tapir',
  'wren',
] as const;

function pick<T>(items: readonly T[], random: () => number): T {
  const item = items[Math.floor(random() * items.length)];
  assert(item !== undefined, 'word list must be non-empty');
  return item;
}

/**
 * Generate a single whimsical `adjective-noun` host name. Pass a custom `random` (e.g. a seeded
 * generator) to make output deterministic in tests/stories.
 */
export function generateHostName(random: () => number = Math.random): string {
  return `${pick(ADJECTIVES, random)}-${pick(NOUNS, random)}`;
}

/** Generate `count` distinct host names — re-rolls collisions so the onboarding hosts never clash. */
export function generateHostNames(count: number, random: () => number = Math.random): string[] {
  assert(count <= ADJECTIVES.length * NOUNS.length, 'requested more host names than there are combinations');
  const names = new Set<string>();
  while (names.size < count) {
    names.add(generateHostName(random));
  }
  return [...names];
}
